import React, { useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Avatar,
  Button,
  IconButton,
  Input,
} from "@chakra-ui/react";
import { ChatIcon, ArrowUpIcon } from "@chakra-ui/icons";
import { FavoriteIconReact, FavoriteBorderIconReact } from "../icons";
import {
  useGetCommentsForPost,
  useToggleReplyLike,
  useCreateReply,
} from "../../apis/experiences";
import { CommentWithFullAuthor } from "../../apis/types";

type Props = {
  postId: string;
  currentDoctorId?: string;
};

type CommentNode = CommentWithFullAuthor & { replies?: CommentNode[] };

const HeartSolid = (props: any) => (
  <FavoriteIconReact color="red" size={16} {...props} />
);
const HeartOutline = (props: any) => (
  <FavoriteBorderIconReact color="gray" size={16} {...props} />
);

// --- build nested tree ---
function buildTree(flat: CommentWithFullAuthor[]): CommentNode[] {
  const map = new Map<string, CommentNode>();
  flat.forEach((r) => map.set(r._id, { ...r, replies: [] }));

  const roots: CommentNode[] = [];
  map.forEach((node) => {
    if (node.parentReply && map.has(node.parentReply)) {
      map.get(node.parentReply)!.replies!.push(node);
    } else {
      roots.push(node);
    }
  });
  return roots;
}

const CommentRow: React.FC<{
  comment: CommentNode;
  postId: string;
  currentDoctorId?: string;
  depth?: number;
}> = ({ comment, postId, currentDoctorId, depth = 0 }) => {
  const toggleLike = useToggleReplyLike();
  const createReply = useCreateReply(postId);

  const [isLiked, setIsLiked] = useState(
    currentDoctorId ? comment.upvotedBy?.includes(currentDoctorId) : false
  );
  const [count, setCount] = useState(comment.upvotedBy?.length ?? 0);
  const [showReplyBox, setShowReplyBox] = useState(false);
  const [replyText, setReplyText] = useState("");

  const handleLike = () => {
    const nextLiked = !isLiked;
    setIsLiked(nextLiked);
    setCount((prev) => (nextLiked ? prev + 1 : prev - 1));

    toggleLike.mutate({
      postId,
      replyId: comment._id,
      currentDoctorId: currentDoctorId ?? "",
      isCurrentlyLiked: !!isLiked,
    });
  };

  const handleSendReply = () => {
    if (!replyText.trim() || !currentDoctorId) return;
    createReply.mutate(
      {
        content: replyText.trim(),
        parentReply: comment._id,
      },
      {
        onSuccess: () => {
          setReplyText("");
          setShowReplyBox(false);
        },
      }
    );
  };

  return (
    <Box pl={depth * 4} mt={depth > 0 ? 3 : 0}>
      <HStack align="flex-start" spacing={3}>
        <Avatar
          size="sm"
          name={comment.author?.name}
          src={comment.author?.avatarUrl}
        />
        <VStack align="flex-start" spacing={1} flex={1}>
          <HStack spacing={1}>
            <Text fontWeight="bold" fontSize="sm">
              {comment.author?.name ?? "Anonymous"}
            </Text>
            <Text fontSize="xs" color="gray.500">
              {new Date(comment.createdAt).toLocaleString()}
            </Text>
          </HStack>

          <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">
            {comment.content}
          </Text>

          <HStack spacing={3} mt={1}>
            <Button
              onClick={handleLike}
              leftIcon={isLiked ? <HeartSolid /> : <HeartOutline />}
              variant="ghost"
              size="xs"
              colorScheme={isLiked ? "red" : "gray"}
            >
              {count}
            </Button>

            <Button
              onClick={() => setShowReplyBox((p) => !p)}
              leftIcon={<ChatIcon />}
              variant="ghost"
              size="xs"
            >
              Reply
            </Button>
          </HStack>

          {showReplyBox && (
            <Box w="100%" mt={2}>
              <HStack align="center">
                <Input
                  size="sm"
                  placeholder={`Reply to ${
                    comment.author?.name || "this comment"
                  }...`}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  borderRadius="full"
                  bg="gray.50"
                  borderColor="gray.200"
                  _focus={{
                    borderColor: "blue.300",
                    boxShadow: "0 0 0 1px #90CDF4",
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && replyText.trim()) {
                      e.preventDefault();
                      handleSendReply();
                    }
                  }}
                />
                <IconButton
                  aria-label="Send reply"
                  icon={<ArrowUpIcon />}
                  size="sm"
                  isRound
                  bg="gray.900"
                  color="white"
                  _hover={{ bg: "black" }}
                  onClick={handleSendReply}
                  isDisabled={!replyText.trim() || createReply.isPending}
                />
              </HStack>
            </Box>
          )}

          {/* render nested replies recursively */}
          {comment.replies && comment.replies.length > 0 && (
            <VStack align="stretch" spacing={2} mt={2}>
              {comment.replies.map((child) => (
                <CommentRow
                  key={child._id}
                  comment={child}
                  postId={postId}
                  currentDoctorId={currentDoctorId}
                  depth={depth + 1}
                />
              ))}
            </VStack>
          )}
        </VStack>
      </HStack>
    </Box>
  );
};

const CommentsSection: React.FC<Props> = ({ postId, currentDoctorId }) => {
  const { data, isLoading, isError } = useGetCommentsForPost(postId);

  if (isLoading) return <Text color="gray.500">Loading comments...</Text>;
  if (isError) return <Text color="red.500">Failed to load comments.</Text>;

  const flat = data ?? [];
  const roots = buildTree(flat);

  return (
    <VStack align="stretch" spacing={3}>
      {roots.map((root) => (
        <CommentRow
          key={root._id}
          comment={root}
          postId={postId}
          currentDoctorId={currentDoctorId}
        />
      ))}
    </VStack>
  );
};

export default CommentsSection;
